import React, { useState } from "react";

import { GRAPHQL_URL } from "../../config";
import styled from "styled-components";
import { useTypedResource } from "../../i18n/languagehandler";

const NewsletterSection = styled.section`
  max-width: 1000px;
  margin: 40px auto 0;
  padding: 0 20px;
`;

const FramedCard = styled.div`
  position: relative;
  background: linear-gradient(180deg, #ffffff 0%, #f9fafb 100%);
  border: 1px solid #e5e7eb;
  border-radius: 16px;
  box-shadow: 0 12px 30px rgba(0, 0, 0, 0.08);
  padding: 28px;
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 24px;
  overflow: hidden;
  @media (max-width: 900px) {
    grid-template-columns: 1fr;
    padding: 20px;
  }

  &:before {
    content: "";
    position: absolute;
    left: 0;
    top: 0;
    bottom: 0;
    width: 6px;
    background: var(--primary);
  }
`;

const NewsletterDescription = styled.p`
  color: var(--text-secondary);
  font-size: 18px;
  line-height: 1.6;
  margin: 0 0 20px 0;
`;

const NewsletterForm = styled.form`
  display: grid;
  gap: 16px;
`;

const FormGroup = styled.div`
  display: grid;
  gap: 8px;
`;

const Label = styled.label`
  color: var(--text);
  font-size: 14px;
  font-weight: 500;
`;

const Input = styled.input`
  padding: 12px 16px;
  border: 1px solid #e5e7eb;
  border-radius: 8px;
  font-size: 16px;
  transition: border-color 0.2s ease;

  &:focus {
    outline: none;
    border-color: var(--primary);
    box-shadow: 0 0 0 3px rgba(55, 90, 127, 0.1);
  }

  &.error {
    border-color: #ef4444;
  }
`;

const Textarea = styled.textarea`
  padding: 12px 16px;
  border: 1px solid #e5e7eb;
  border-radius: 8px;
  font-size: 16px;
  min-height: 120px;
  resize: vertical;
  transition: border-color 0.2s ease;

  &:focus {
    outline: none;
    border-color: var(--primary);
    box-shadow: 0 0 0 3px rgba(55, 90, 127, 0.1);
  }
`;

const SubmitButton = styled.button`
  background: var(--primary);
  color: #fff;
  border: none;
  padding: 12px 24px;
  border-radius: 8px;
  font-size: 16px;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.2s ease;

  &:hover:not(:disabled) {
    background: var(--primary-dark);
    transform: translateY(-1px);
    box-shadow: 0 4px 12px rgba(55, 90, 127, 0.3);
  }

  &:disabled {
    opacity: 0.6;
    cursor: not-allowed;
  }
`;

const Message = styled.div<{ type: "success" | "error" }>`
  padding: 12px 16px;
  border-radius: 8px;
  font-size: 14px;
  font-weight: 500;
  background: ${(props) => (props.type === "success" ? "#d1fae5" : "#fee2e2")};
  color: ${(props) => (props.type === "success" ? "#065f46" : "#dc2626")};
  border: 1px solid
    ${(props) => (props.type === "success" ? "#a7f3d0" : "#fecaca")};
`;

const LectureRequest: React.FC = () => {
  const res = useTypedResource<{
    newsletterTitle: string;
    newsletterDescription: string;
    newsletterFullName: string;
    newsletterEmail: string;
    newsletterDescriptionLabel: string;
    newsletterSubscribe: string;
    newsletterSuccess: string;
    newsletterError: string;
    newsletterEmailExists: string;
  }>("contact");

  const [newsletterForm, setNewsletterForm] = useState({
    fullName: "",
    email: "",
    description: "",
  });
  const [newsletterMessage, setNewsletterMessage] = useState<{
    type: "success" | "error";
    text: string;
  } | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleNewsletterSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsSubmitting(true);
    setNewsletterMessage(null);

    try {
      const response = await fetch(GRAPHQL_URL, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          query: `
            mutation CreateLectureRequest($fullName: String!, $email: String!, $description: String) {
              createLectureRequest(fullName: $fullName, email: $email, description: $description)
            }
          `,
          variables: {
            fullName: newsletterForm.fullName,
            email: newsletterForm.email,
            description: newsletterForm.description || null,
          },
        }),
      });

      const result = await response.json();

      if (result.errors) {
        const errorMessage = result.errors[0].message as string;
        if (errorMessage.includes("already exists")) {
          setNewsletterMessage({
            type: "error",
            text: res.newsletterEmailExists,
          });
        } else {
          setNewsletterMessage({
            type: "error",
            text: res.newsletterError,
          });
        }
      } else {
        setNewsletterMessage({
          type: "success",
          text: res.newsletterSuccess,
        });
        setNewsletterForm({ fullName: "", email: "", description: "" });
      }
    } catch (error) {
      setNewsletterMessage({
        type: "error",
        text: res.newsletterError,
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setNewsletterForm((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  return (
    <NewsletterSection>
      <FramedCard>
        <div>
          <NewsletterDescription>
            {res.newsletterDescription}
          </NewsletterDescription>
        </div>
        <div>
          <NewsletterForm onSubmit={handleNewsletterSubmit}>
            <FormGroup>
              <Label htmlFor="fullName">{res.newsletterFullName}</Label>
              <Input
                type="text"
                id="fullName"
                name="fullName"
                value={newsletterForm.fullName}
                onChange={handleInputChange}
                required
                disabled={isSubmitting}
                placeholder={res.newsletterFullName}
                autoComplete="name"
              />
            </FormGroup>

            <FormGroup>
              <Label htmlFor="email">{res.newsletterEmail}</Label>
              <Input
                type="email"
                id="email"
                name="email"
                value={newsletterForm.email}
                onChange={handleInputChange}
                required
                disabled={isSubmitting}
                placeholder={res.newsletterEmail}
                autoComplete="email"
              />
            </FormGroup>

            <FormGroup>
              <Label htmlFor="description">
                {res.newsletterDescriptionLabel}
              </Label>
              <Textarea
                id="description"
                name="description"
                value={newsletterForm.description}
                onChange={(e) =>
                  setNewsletterForm((prev) => ({
                    ...prev,
                    description: e.target.value,
                  }))
                }
                placeholder={res.newsletterDescriptionLabel}
                disabled={isSubmitting}
              />
            </FormGroup>

            <SubmitButton type="submit" disabled={isSubmitting}>
              {isSubmitting ? "Subscribing..." : res.newsletterSubscribe}
            </SubmitButton>

            {newsletterMessage && (
              <Message
                type={newsletterMessage.type}
                role="status"
                aria-live="polite"
              >
                {newsletterMessage.text}
              </Message>
            )}
          </NewsletterForm>
        </div>
      </FramedCard>
    </NewsletterSection>
  );
};

export default LectureRequest;
